import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Section } from "@/components/molecules/section";
import { UserAvatar } from "@/components/molecules/user-avatar";
import { useUser, useUpdateProfile } from "@/features/auth/hooks/use-user";
import { useTheme } from "@/features/utils/theme";
import { useLanguage, languages, useTranslation } from "@/features/utils/i18n";
import { PageLayout } from "../backoffice/layout";
import { Upload } from "@phosphor-icons/react";
import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";

export const PreferencesPage = () => {
  const { t } = useTranslation("preferences");
  const { data: user } = useUser();
  const updateProfile = useUpdateProfile();
  const { theme, setTheme } = useTheme();
  const { currentLanguage, changeLanguage } = useLanguage();

  const [fullName, setFullName] = useState(user?.full_name || "");
  const [avatarPreview, setAvatarPreview] = useState<string | null>(
    user?.preferences?.avatar || null,
  );
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Update local state when user data loads
  useEffect(() => {
    if (user) {
      setFullName(user.full_name || "");
      setAvatarPreview(user.preferences?.avatar || null);
    }
  }, [user]);

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error(t("messages.selectImage"));
      return;
    }

    // Validate file size (max 2MB for avatars)
    if (file.size > 2 * 1024 * 1024) {
      toast.error(t("messages.imageSizeLimit"));
      return;
    }

    setAvatarFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveAvatar = () => {
    setAvatarPreview(null);
    setAvatarFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleThemeChange = (newTheme: string) => {
    setTheme(newTheme as "" | "dark" | "light");
    toast.success(t("messages.themeUpdated"));
  };

  const handleLanguageChange = (newLanguage: string) => {
    changeLanguage(newLanguage);
    toast.success(t("messages.languageUpdated"));
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const updates: {
        full_name?: string;
        avatar?: string;
      } = {};

      // Check for name changes
      if (fullName.trim() !== user?.full_name) {
        updates.full_name = fullName.trim();
      }

      // Check for avatar changes
      if (avatarFile) {
        const reader = new FileReader();
        reader.onloadend = async () => {
          updates.avatar = reader.result as string;

          await updateProfile.mutateAsync(updates);
          toast.success(t("messages.profileUpdated"));
          setAvatarFile(null);
        };
        reader.readAsDataURL(avatarFile);
      } else if (avatarPreview === null && user?.preferences?.avatar) {
        // Avatar was removed
        updates.avatar = "";

        await updateProfile.mutateAsync(updates);
        toast.success(t("messages.profileUpdated"));
      } else if (Object.keys(updates).length > 0) {
        await updateProfile.mutateAsync(updates);
        toast.success(t("messages.profileUpdated"));
      }
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("messages.profileUpdateFailed"),
      );
    }
  };

  const hasProfileChanges =
    fullName.trim() !== (user?.full_name || "") ||
    avatarFile !== null ||
    (avatarPreview === null && user?.preferences?.avatar !== null);

  if (!user) {
    return (
      <PageLayout
        width="narrow"
        title={t("title")}
        description={t("description")}
      >
        <div className="text-center text-muted-foreground">
          {t("messages.loading")}
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      width="narrow"
      title={t("title")}
      description={t("description")}
    >
      <div className="space-y-8">
        {/* Profile */}
        <form onSubmit={handleSaveProfile}>
          <Section
            title={t("profile.title")}
            description={t("profile.description")}
          >
            <div className="space-y-6">
              {/* Avatar */}
              <div className="space-y-2">
                <Label>{t("profile.avatar")}</Label>
                <div className="flex items-center gap-4">
                  <UserAvatar
                    avatar={avatarPreview}
                    name={user.full_name || user.email}
                    email={user.email}
                    size="xl"
                  />
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {t("profile.uploadAvatar")}
                    </Button>
                    {avatarPreview && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleRemoveAvatar}
                      >
                        {t("profile.removeAvatar")}
                      </Button>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleAvatarChange}
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t("profile.avatarHelp")}
                </p>
              </div>

              {/* Full Name */}
              <div className="space-y-2">
                <Label htmlFor="fullName">{t("profile.fullName")}</Label>
                <Input
                  id="fullName"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder={t("profile.fullNamePlaceholder")}
                />
              </div>

              {/* Save Button */}
              {hasProfileChanges && (
                <div className="flex gap-2">
                  <Button type="submit" disabled={updateProfile.isPending}>
                    {updateProfile.isPending
                      ? t("profile.saving")
                      : t("profile.saveChanges")}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setFullName(user.full_name || "");
                      setAvatarPreview(user.preferences?.avatar || null);
                      setAvatarFile(null);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = "";
                      }
                    }}
                  >
                    {t("profile.cancel")}
                  </Button>
                </div>
              )}
            </div>
          </Section>
        </form>

        {/* Appearance */}
        <Section
          title={t("appearance.title")}
          description={t("appearance.description")}
        >
          <div className="space-y-2">
            <Label htmlFor="theme">{t("appearance.theme")}</Label>
            <Select value={theme} onValueChange={handleThemeChange}>
              <SelectTrigger id="theme">
                <SelectValue placeholder={t("appearance.selectTheme")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="light">{t("appearance.light")}</SelectItem>
                <SelectItem value="dark">{t("appearance.dark")}</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {t("appearance.themeHelp")}
            </p>
          </div>
        </Section>

        {/* Language */}
        <Section
          title={t("language.title")}
          description={t("language.description")}
        >
          <div className="space-y-2">
            <Label htmlFor="language">{t("language.language")}</Label>
            <Select
              value={currentLanguage.toLowerCase()}
              onValueChange={handleLanguageChange}
            >
              <SelectTrigger id="language">
                <SelectValue placeholder={t("language.selectLanguage")} />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(languages).map(([code, name]) => (
                  <SelectItem key={code} value={code.toLowerCase()}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {t("language.languageHelp")}
            </p>
          </div>
        </Section>
      </div>
    </PageLayout>
  );
};
